import { useCallback, useMemo, useState, useEffect } from 'react';
import { faArrowRotateRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, NotificationDispatch, StyledFlex, Tooltip } from '@tryhackme/thm-ui-components';
import { isFuture } from 'date-fns';
import { formatNetworkState, getNewNodesStructure } from 'src/features/room/helpers';
import { ApiResponseStatus } from 'src/common/enums';
import { NetworkState } from 'src/common/enums/networks';
import { calculateExpiryTimeLeft } from 'src/common/helpers/date/date';
import { invalidateNetworkDetailsCache, useResetNetworkMutation, useGetNetworkQuery, useStartNetworkMutation, useExtendNetworkMutation } from 'src/features/room/room.slice';
import { addSocketListener, removeSocketListener } from 'src/common/sockets';
import { ROOM_EVENT } from 'src/features/room/room.constants';
import { RoomEvent } from 'src/features/room/room.types';
import { RoomEventType } from 'src/common/enums/rooms';
import { hasResponseData } from 'src/common/helpers/slices';
import { useAppDispatch } from 'src/app/hooks';
import { setNetworkDetails } from 'src/app/reducers/room/room.reducer';
import { NetworkDisplay } from './components/network-display';
import { StyledNetworks, StyledControls, StyledWrapperButton, StyledTooltip, StyledNetworkState, StyledNetworkDisplay, StyledNetworkTime } from './networks.styles';
import { NetworksProps } from './networks.types';
import { NetworkResetModal } from './components/network-reset-modal';
import { NetworkUpTime } from './components/network-up-time';
import { ExtendTimeLeft } from './components/extend-time-left';
export const LegacyNetworks = ({
  isJoined,
  taskDetails,
  roomId
}: NetworksProps) => {
  const [openNetworkResetModal, setOpenNetworkResetModal] = useState<boolean>(false);
  const [resetNetwork, {
    isLoading: resetIsLoading
  }] = useResetNetworkMutation();
  const [startNetwork, {
    isLoading: startIsLoading
  }] = useStartNetworkMutation();
  const {
    data: networkDetails
  } = useGetNetworkQuery(roomId);
  const [extendNetwork, {
    isLoading: extendIsLoading
  }] = useExtendNetworkMutation();
  const dispatch = useAppDispatch();
  const networkState = networkDetails?.state ?? NetworkState.STOPPED;
  const nodes = networkDetails?.nodes;
  const edges = networkDetails?.connections.map(connection => ({
    ...connection,
    id: connection._id
  }));
  const networkId = networkDetails?._id;
  const resetsRequired = networkDetails?.resetsRequired;
  const lastBooted = networkDetails?.lastBooted;
  const resets = networkDetails?.resets;
  const resetCount = resets?.length ?? 0;
  const pauseTime = networkDetails?.pauseTime;
  const extendTime = useMemo(() => pauseTime && isFuture(new Date(pauseTime)) ? new Date(pauseTime) : null, [pauseTime]);
  const {
    textState,
    dataState
  } = formatNetworkState(networkState);
  const handleExtendTime = useCallback(async () => {
    if (!extendTime) return;
    const timeRemaining = calculateExpiryTimeLeft(new Date(extendTime), new Date());
    if (timeRemaining.hours < 1) {
      await extendNetwork(networkId ?? '');
      NotificationDispatch('success', 'Success! Network expire time updated.');
    } else {
      NotificationDispatch('error', `Uh-oh! You can only increase the expiry time for a maximum of two hours`);
    }
  }, [extendNetwork, extendTime, networkId]);
  const handleStart = useCallback(async () => {
    if (networkState !== NetworkState.STOPPED) return;
    const response = await startNetwork(networkId ?? '');
    let errorOccurred = true;
    if (hasResponseData<boolean>(response) && response.data.data) {
      errorOccurred = false;
    }
    if (errorOccurred) {
      NotificationDispatch('error', 'Uh-no! Failed to start the network.');
    } else {
      NotificationDispatch('success', 'Started. The network has been scheduled to start. This process may take up to 5 minutes');
    }
  }, [networkId, networkState, startNetwork]);
  const handleReset = useCallback(async () => {
    const response = await resetNetwork(networkId ?? '');
    if ('error' in response || !('data' in response)) throw new Error('Something went wrong.');
    const message = response.data.data?.message ?? '';
    if (response.data.status === ApiResponseStatus.SUCCESS) {
      NotificationDispatch('success', `Success! ${message}`);
    } else {
      NotificationDispatch('error', `Uh-oh! ${message}`);
    }
  }, [networkId, resetNetwork]);
  useEffect(() => {
    const handleNetworkState = (data: unknown) => {
      const eventData = (data as RoomEvent);
      if (eventData.type === RoomEventType.NETWORK_RESETTING && eventData.networkId === networkDetails?._id) {
        setOpenNetworkResetModal(true);
        invalidateNetworkDetailsCache();
      }
    };
    if (isJoined) addSocketListener(ROOM_EVENT, handleNetworkState);
    return () => {
      removeSocketListener(ROOM_EVENT);
    };
  }, [networkDetails?._id, isJoined]);
  useEffect(() => {
    dispatch(setNetworkDetails(networkDetails));
  }, [dispatch, networkDetails]);
  return <StyledNetworks data-testid="networks" data-sentry-element="StyledNetworks" data-sentry-component="LegacyNetworks" data-sentry-source-file="legacy-networks.tsx">
      <StyledNetworkDisplay id="network-diagram" data-sentry-element="StyledNetworkDisplay" data-sentry-source-file="legacy-networks.tsx">
        {nodes && <NetworkDisplay nodes={getNewNodesStructure(nodes, taskDetails)} edges={edges} />}
      </StyledNetworkDisplay>

      {isJoined && networkDetails && <StyledNetworkState>
          Network state: <span data-state={dataState}>{textState}</span>
        </StyledNetworkState>}

      {isJoined && networkDetails && <StyledControls>
          <StyledFlex gap="1.6rem" justifyContent="flex-start">
            <StyledWrapperButton>
              <Button
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={handleStart} disabled={networkState !== NetworkState.STOPPED} isLoading={startIsLoading}>
                <FontAwesomeIcon icon={faPlay} />
                Start
              </Button>
              <StyledTooltip data-wrapper="tooltip" data-tooltip="first">
                <Tooltip>The network will go to sleep if there is no activity detected</Tooltip>
              </StyledTooltip>
            </StyledWrapperButton>
            <StyledWrapperButton>
              <Button color="hint"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={handleExtendTime} id="network-diagram-extend" isLoading={extendIsLoading}>
                <FontAwesomeIcon icon={faPlus} /> Extend {extendTime ? <ExtendTimeLeft pauseTime={extendTime} /> : ''}
              </Button>
              <StyledTooltip data-wrapper="tooltip">
                <Tooltip>The network will go to sleep if there is no activity</Tooltip>
              </StyledTooltip>
            </StyledWrapperButton>

            <StyledWrapperButton>
              <Button isLoading={resetIsLoading} color="error"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={handleReset} id="network-diagram-reset" disabled={!resetsRequired}>
                <FontAwesomeIcon icon={faArrowRotateRight} /> {`Reset ${resetCount}/${resetsRequired ?? 0}`}
              </Button>
              <StyledTooltip data-wrapper="tooltip">
                <Tooltip>When there are enough votes, the network will reset to its default state</Tooltip>
              </StyledTooltip>
            </StyledWrapperButton>
          </StyledFlex>
          <StyledNetworkTime>
            Network up time: {lastBooted ? <NetworkUpTime networkUpTime={new Date(lastBooted)} /> : ' -'}
          </StyledNetworkTime>
        </StyledControls>}
      <NetworkResetModal open={openNetworkResetModal} onOpenChange={setOpenNetworkResetModal} data-sentry-element="NetworkResetModal" data-sentry-source-file="legacy-networks.tsx" />
    </StyledNetworks>;
};